import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { Select, SelectChangeEvent } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ContentListResponseDto } from 'src/app/shared/dtos/content-list.dto';
import { map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ISelectOption } from 'src/app/shared/interfaces/primeng.interface';
import { MachineDto, MachineGroupDto, MachineRangeDto } from 'src/app/shared/dtos/machine.dto';
import { GroupWithMachines } from 'src/app/shared/types/machine.type';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TruncatePipe } from 'src/app/shared/pipes/truncate.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-machine-range',
  imports: [
    RouterLink,
    Select,
    FormsModule,
    AsyncPipe,
    TranslocoPipe,
    TruncatePipe,
  ],
  templateUrl: './machine-range.html',
  styleUrl: './machine-range.scss'
})
export class MachineRange implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly machineRangeId$ = this.activatedRoute.params.pipe(
    map(params => params['machine-range-id'])
  );

  protected readonly contentList$: Observable<ContentListResponseDto> = this.activatedRoute.parent!.data.pipe(
    map(data => data['contentList']),
  );

  protected readonly ranges$: Observable<ISelectOption[] | undefined> = this.contentList$.pipe(
    map(contentList => {
        if (!contentList) {
          void this.router.navigate(['/not-found']);
          return [];
        }

        const ranges = contentList.content.map((range: MachineRangeDto) => ({
          name: range.name,
          code: range.id
        }));

        return ranges.sort((a, b) => a.name.localeCompare(b.name));
      })
  );

  protected readonly groups$ = combineLatest([
    this.contentList$,
    this.machineRangeId$
  ]).pipe(
    map(([contentList, machineRangeId]) => {
      if (!contentList) {
        void this.router.navigate(['/not-found']);
        return [];
      }

      const range = contentList.content.find((range: MachineRangeDto) => range.id === Number(machineRangeId));

      if (!range) {
        void this.router.navigate(['/not-found']);
        return [];
      }

      const machineIds = range.children.map((machine: MachineDto) => machine.id);
      const assignedMachineIds = range.groups.flatMap((group: MachineGroupDto) => group.ids);
      const unassignedMachineIds = machineIds.filter((id: number) => !assignedMachineIds.includes(id));

      const otherGroup = {
        name: this.transloco.translate('machineRange.other'),
        ids: unassignedMachineIds,
        machines: range.children
          .filter((machine: MachineDto) => unassignedMachineIds.includes(machine.id))
          .sort(this.compareByOrdinal),
        ordinal: 9999
      };

      const groups: GroupWithMachines[] = range.groups.map((group: MachineGroupDto) => ({
        ...group,
        machines: range.children
          .filter((machine: MachineDto) => group.ids.includes(machine.id))
          .sort(this.compareByOrdinal)
      }));
      
      if(groups.length == 0){
        const emptyGroup = {
          name: '',
          ids: unassignedMachineIds,
          machines: range.children
            .filter((machine: MachineDto) => unassignedMachineIds.includes(machine.id))
            .sort(this.compareByOrdinal),
          ordinal: 9999
        };
        return [emptyGroup];
      }

      return [...groups, otherGroup].filter((group: GroupWithMachines) => {
        return group.machines.length > 0;
      }).sort((a, b) => a.ordinal - b.ordinal);
    })
  );

  private compareByOrdinal = (a: MachineDto, b: MachineDto): number => {
    const ao = a.ordinal ?? Number.POSITIVE_INFINITY;
    const bo = b.ordinal ?? Number.POSITIVE_INFINITY;
    return ao - bo;
  };

  protected selectedRange$ = new BehaviorSubject<ISelectOption | undefined>(undefined);

  ngOnInit(): void {
    combineLatest([
      this.ranges$,
      this.machineRangeId$
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([ranges, machineRangeId]) => {
      if (ranges && machineRangeId) {
        const selectedRange = ranges.find((range: ISelectOption) => range.code === Number(machineRangeId));
        this.selectedRange$.next(selectedRange || {
          name: this.transloco.translate('machineRange.selectARange'),
          code: null
        });

        this.analyticsService.analyticsPageData = {
          itemId: parseInt(machineRangeId),
          itemName: selectedRange?.name ?? 'range',
          jcbRangeId: parseInt(machineRangeId),
          jcbRangeName: selectedRange?.name ?? 'range',
        }

        this.analyticsService.trackPageLoad();
      }
    });
  }

  protected changePage(event: SelectChangeEvent): void {
    void this.router.navigate(['machine-range', event.value.code]);

    this.analyticsService.analyticsPageData = {
      itemId: event.value.code,
      itemName: event.value.name ?? 'range',
      jcbRangeId: event.value.code,
      jcbRangeName: event.value.name ?? 'range',
    }

    this.analyticsService.trackPageLoad();
  }
}
